import { Component } from 'preact';
import { connect } from 'unistore/preact';
import { Text, Localizer } from 'preact-i18n';
import cx from 'classnames';
import update from 'immutability-helper';

import BaseEditBox from '../baseEditBox';
import SelectDeviceFeature from '../../device/SelectDeviceFeature';
import { HOUSE_VIEW_GALLERY } from './gallery';
import { resolveHouseViewImage } from './HouseViewBox';
import { prepareImageUpload } from '../../../utils/downscaleImage';
import { startPinDrag, percentFromPointer, clampPercent } from './pinDrag';
import style from './style.module.css';

// Keyboard nudge on a focused pin: one percent per key press, a tenth with
// shift for the last bit of precision.
const KEYBOARD_STEP_PCT = 1;
const KEYBOARD_FINE_STEP_PCT = 0.1;
const KEYBOARD_MOVES = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 }
};

class EditHouseViewBox extends Component {
  updateName = e => {
    this.props.updateBoxConfig(this.props.x, this.props.y, { name: e.target.value });
  };

  selectGalleryImage = key => {
    this.props.updateBoxConfig(this.props.x, this.props.y, { image: `gallery:${key}` });
  };

  uploadImage = async e => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }
    this.setState({ uploading: true, uploadError: false });
    try {
      const { contentType, data } = await prepareImageUpload(file);
      const { id } = await this.props.httpClient.post(`/api/v1/dashboard_asset/${this.props.homeDashboard.selector}`, {
        content_type: contentType,
        data
      });
      this.props.updateBoxConfig(this.props.x, this.props.y, { image: `asset:${id}` });
    } catch (err) {
      console.error(err);
      this.setState({ uploadError: true });
    }
    this.setState({ uploading: false });
  };

  refreshImagePreview = async () => {
    try {
      const imageUrl = await resolveHouseViewImage(this.props.httpClient, this.props.box.image);
      this.setState({ imageUrl });
    } catch (e) {
      console.error(e);
    }
  };

  addPinOnImage = e => {
    const position = percentFromPointer(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY);
    const pins = this.props.box.pins || [];
    this.props.updateBoxConfig(this.props.x, this.props.y, {
      pins: [...pins, { ...position, device_feature: undefined }]
    });
  };

  attachImage = element => {
    this.imageElement = element;
  };

  // the image and its pin markers: the surface a drag lives on
  attachImageWrapper = element => {
    this.imageWrapperElement = element;
  };

  getImageRect = () => (this.imageElement ? this.imageElement.getBoundingClientRect() : null);

  getSurface = () => this.imageWrapperElement || null;

  // Dragging a pin marker on the image moves it: no more remove & place again.
  onPinPointerDown = (index, e) => {
    startPinDrag(e, {
      draggingClass: style.editPinNumberDragging,
      getImageRect: this.getImageRect,
      getSurface: this.getSurface,
      onMove: (marker, position) => {
        marker.style.left = `${position.x_pct}%`;
        marker.style.top = `${position.y_pct}%`;
      },
      onDrop: position => this.updatePin(index, position),
      onCancel: marker => {
        const pin = this.props.box.pins[index];
        marker.style.left = `${pin.x_pct}%`;
        marker.style.top = `${pin.y_pct}%`;
      }
    });
  };

  // A focused pin also moves with the arrow keys — precise, and the only way
  // to move one without a pointer.
  movePinWithKeyboard = (index, e) => {
    const move = KEYBOARD_MOVES[e.key];
    if (!move) {
      return;
    }
    e.preventDefault();
    const step = e.shiftKey ? KEYBOARD_FINE_STEP_PCT : KEYBOARD_STEP_PCT;
    const pin = this.props.box.pins[index];
    this.updatePin(index, {
      x_pct: clampPercent(pin.x_pct + move.x * step),
      y_pct: clampPercent(pin.y_pct + move.y * step)
    });
  };

  updatePin = (index, data) => {
    const newPins = update(this.props.box.pins, { [index]: { $merge: data } });
    this.props.updateBoxConfig(this.props.x, this.props.y, { pins: newPins });
  };

  removePin = index => {
    const newPins = update(this.props.box.pins, { $splice: [[index, 1]] });
    this.props.updateBoxConfig(this.props.x, this.props.y, { pins: newPins });
  };

  componentDidMount() {
    this.refreshImagePreview();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.box.image !== this.props.box.image) {
      this.refreshImagePreview();
    }
  }

  render(props, { imageUrl, uploading, uploadError }) {
    const selectedImage = props.box.image || '';
    const pins = props.box.pins || [];
    return (
      <BaseEditBox {...props} titleKey="dashboard.boxTitle.house-view">
        <div class="form-group">
          <label class="form-label">
            <Text id="dashboard.boxes.house-view.editNameLabel" />
          </label>
          <Localizer>
            <input
              type="text"
              class="form-control"
              placeholder={<Text id="dashboard.boxes.house-view.editNamePlaceholder" />}
              value={props.box.name}
              onInput={this.updateName}
            />
          </Localizer>
        </div>
        <div class="form-group">
          <label class="form-label">
            <Text id="dashboard.boxes.house-view.editGalleryLabel" />
          </label>
          <div class={style.galleryGrid}>
            {HOUSE_VIEW_GALLERY.map(galleryEntry => (
              <div
                class={cx(style.galleryItem, {
                  [style.galleryItemSelected]: selectedImage === `gallery:${galleryEntry.key}`
                })}
                onClick={() => this.selectGalleryImage(galleryEntry.key)}
              >
                <img src={galleryEntry.url} alt={galleryEntry.key} />
              </div>
            ))}
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">
            <Text id="dashboard.boxes.house-view.editUploadLabel" />
          </label>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="form-control"
            onChange={this.uploadImage}
          />
          {uploading && (
            <small class="d-block mt-1">
              <Text id="dashboard.boxes.house-view.uploading" />
            </small>
          )}
          {uploadError && (
            <div class="alert alert-danger mt-2">
              <Text id="dashboard.boxes.house-view.uploadError" />
            </div>
          )}
        </div>
        {imageUrl && (
          <div class="form-group">
            <label class="form-label">
              <Text id="dashboard.boxes.house-view.editPinsLabel" />
            </label>
            <small class="d-block mb-2">
              <Text id="dashboard.boxes.house-view.editPinsDescription" />
            </small>
            <div class={style.imageWrapper} ref={this.attachImageWrapper}>
              <img
                class={cx(style.image, style.editImage)}
                src={imageUrl}
                alt=""
                ref={this.attachImage}
                onClick={this.addPinOnImage}
              />
              {pins.map((pin, index) => (
                <Localizer>
                  <span
                    class={style.editPinNumber}
                    style={`left: ${pin.x_pct}%; top: ${pin.y_pct}%;`}
                    role="button"
                    tabIndex="0"
                    title={<Text id="dashboard.boxes.house-view.editPinMoveHelp" />}
                    aria-label={<Text id="dashboard.boxes.house-view.editPinLabel" fields={{ index: index + 1 }} />}
                    onPointerDown={e => this.onPinPointerDown(index, e)}
                    onKeyDown={e => this.movePinWithKeyboard(index, e)}
                  >
                    {index + 1}
                  </span>
                </Localizer>
              ))}
            </div>
            {pins.map((pin, index) => (
              <div class="card p-3 mt-2 mb-0">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong>
                    <Text id="dashboard.boxes.house-view.editPinLabel" fields={{ index: index + 1 }} />
                  </strong>
                  <button class="btn btn-sm btn-outline-danger" onClick={() => this.removePin(index)}>
                    <i class="fe fe-trash" />
                  </button>
                </div>
                <div class="form-group">
                  <label class="form-label">
                    <Text id="dashboard.boxes.house-view.editPinFeatureLabel" />
                  </label>
                  <SelectDeviceFeature
                    value={pin.device_feature}
                    onDeviceFeatureChange={feature =>
                      this.updatePin(index, { device_feature: feature ? feature.selector : undefined })
                    }
                  />
                </div>
                <div class="form-group mb-0">
                  <label class="form-label">
                    <Text id="dashboard.boxes.house-view.editPinLabelLabel" />
                  </label>
                  <Localizer>
                    <input
                      type="text"
                      class="form-control"
                      placeholder={<Text id="dashboard.boxes.house-view.editPinLabelPlaceholder" />}
                      value={pin.label}
                      onInput={e => this.updatePin(index, { label: e.target.value })}
                    />
                  </Localizer>
                </div>
              </div>
            ))}
          </div>
        )}
      </BaseEditBox>
    );
  }
}

export default connect('httpClient', {})(EditHouseViewBox);
